import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Trash2, Plus, Minus, ShoppingCart, Send, CheckCircle, ArrowLeft } from 'lucide-react'
import { Container } from '@/components/layout'
import { useQuoteBasketStore } from '@/stores/quoteBasketStore'
import { supabase } from '@/lib/supabase'

const QUOTE_CONTACT_STORAGE_KEY = 'engimarine-quote-contact'

interface SavedContactInfo {
  company_name: string
  contact_name: string
  email: string
  phone: string
}

const quoteRequestSchema = z.object({
  company_name: z.string().min(2, 'Company name is required'),
  contact_name: z.string().min(2, 'Contact name is required'),
  email: z.string().email('Please enter a valid email'),
  phone: z.string().optional(),
  message: z.string().optional(),
})

type QuoteRequestFormData = z.infer<typeof quoteRequestSchema>

function getSavedContactInfo(): SavedContactInfo | null {
  try {
    const saved = localStorage.getItem(QUOTE_CONTACT_STORAGE_KEY)
    return saved ? JSON.parse(saved) : null
  } catch {
    return null
  }
}

function saveContactInfo(data: QuoteRequestFormData) {
  try {
    const contactInfo: SavedContactInfo = {
      company_name: data.company_name,
      contact_name: data.contact_name,
      email: data.email,
      phone: data.phone || '',
    }
    localStorage.setItem(QUOTE_CONTACT_STORAGE_KEY, JSON.stringify(contactInfo))
  } catch {
    // Ignore storage errors
  }
}

export function QuoteBasketPage() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const { items, removeItem, updateQuantity, clearBasket } = useQuoteBasketStore()

  const savedContact = getSavedContactInfo()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<QuoteRequestFormData>({
    resolver: zodResolver(quoteRequestSchema),
    defaultValues: {
      company_name: savedContact?.company_name || '',
      contact_name: savedContact?.contact_name || '',
      email: savedContact?.email || '',
      phone: savedContact?.phone || '',
      message: '', // Always empty - user requested this to be asked every time
    },
  })

  const onSubmit = async (data: QuoteRequestFormData) => {
    try {
      // Save contact info to localStorage for future use
      saveContactInfo(data)

      // First, create the quote request
      const { data: quoteRequest, error: quoteError } = await supabase
        .from('quote_requests')
        .insert({
          company_name: data.company_name,
          contact_name: data.contact_name,
          email: data.email,
          phone: data.phone || null,
          message: data.message || null,
          status: 'pending',
        })
        .select('id')
        .single()

      if (quoteError) throw quoteError

      // Then, insert all basket items linked to this quote request
      const quoteItems = items.map((item) => ({
        quote_request_id: quoteRequest.id,
        product_id: item.product.id,
        configuration: item.configuration,
        quantity: item.quantity,
      }))

      const { error: itemsError } = await supabase
        .from('quote_request_items')
        .insert(quoteItems)

      if (itemsError) throw itemsError

      setIsSubmitted(true)
      clearBasket()
    } catch (error) {
      console.error('Failed to submit quote request:', error)
      alert('Failed to submit quote request. Please try again.')
    }
  }

  if (isSubmitted) {
    return (
      <section className="py-16 lg:py-24">
        <Container size="small">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-8 lg:p-12 text-center"
          >
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-secondary-900 mb-4">
              Quote Request Submitted!
            </h1>
            <p className="text-secondary-600 mb-8 max-w-md mx-auto">
              Thank you for your inquiry. Our team will review your request and
              get back to you within 24-48 hours.
            </p>
            <Link to="/products" className="btn-primary px-6 py-3">
              Continue Browsing
            </Link>
          </motion.div>
        </Container>
      </section>
    )
  }

  if (items.length === 0) {
    return (
      <section className="py-16 lg:py-24">
        <Container size="small">
          <div className="text-center">
            <ShoppingCart className="w-20 h-20 text-secondary-300 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-secondary-900 mb-4">
              Your Quote Basket is Empty
            </h1>
            <p className="text-secondary-600 mb-8">
              Add products to your basket to request a quote.
            </p>
            <Link to="/products" className="btn-primary px-6 py-3">
              Browse Products
            </Link>
          </div>
        </Container>
      </section>
    )
  }

  return (
    <>
      {/* Hero */}
      <section className="bg-secondary-900 text-white py-12 lg:py-16">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-secondary-400 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Continue Shopping
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold">Quote Basket</h1>
          </motion.div>
        </Container>
      </section>

      {/* Basket Content */}
      <section className="py-12 lg:py-16">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Items */}
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="card p-4 lg:p-6"
                >
                  <div className="flex gap-4">
                    <div className="w-24 h-24 bg-secondary-100 rounded-lg flex-shrink-0 flex items-center justify-center">
                      <ShoppingCart className="w-8 h-8 text-secondary-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-secondary-900 mb-1">
                        {item.product.name}
                      </h3>
                      <p className="text-sm text-secondary-500 mb-3">
                        Code: {item.product.code}
                      </p>

                      {/* Configuration Summary */}
                      {item.configuration.dimensions && (
                        <div className="text-xs text-secondary-600 mb-2">
                          <span className="font-medium">Size:</span>{' '}
                          {item.configuration.dimensions.width} x{' '}
                          {item.configuration.dimensions.height}{' '}
                          {item.configuration.dimensions.unit}
                        </div>
                      )}

                      <div className="flex items-center gap-4">
                        {/* Quantity */}
                        <div className="flex items-center border border-secondary-200 rounded-lg">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-2 hover:bg-secondary-50 transition-colors"
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="px-4 py-2 font-medium">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-2 hover:bg-secondary-50 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Remove */}
                        <button
                          onClick={() => removeItem(item.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Quote Form */}
            <div className="lg:col-span-1">
              <div className="card p-6 sticky top-24">
                <h2 className="text-xl font-bold text-secondary-900 mb-6">
                  Request Quote
                </h2>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      {...register('company_name')}
                      className="input"
                      placeholder="Your company"
                    />
                    {errors.company_name && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.company_name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                      Contact Name *
                    </label>
                    <input
                      type="text"
                      {...register('contact_name')}
                      className="input"
                      placeholder="Your name"
                    />
                    {errors.contact_name && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.contact_name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                      Email *
                    </label>
                    <input
                      type="email"
                      {...register('email')}
                      className="input"
                      placeholder="your@email.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                      Phone
                    </label>
                    <input
                      type="tel"
                      {...register('phone')}
                      className="input"
                      placeholder="+1 234 567 8900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                      Additional Notes
                    </label>
                    <textarea
                      {...register('message')}
                      rows={3}
                      className="input resize-none"
                      placeholder="Any specific requirements..."
                    />
                  </div>

                  <div className="pt-4 border-t border-secondary-100">
                    <div className="flex justify-between text-sm mb-4">
                      <span className="text-secondary-600">Items in basket:</span>
                      <span className="font-semibold">{items.length}</span>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full py-3"
                    >
                      {isSubmitting ? (
                        'Submitting...'
                      ) : (
                        <>
                          Submit Quote Request
                          <Send className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  )
}
